import { useState } from 'react';
import { useLocationTreeLazyLoader, useParentCategoryTree } from '../api/useLibraryApi';
import type { FlatTreeNode } from '../types';

/**
 * Composite hook for admin form pages that need both location and category trees.
 * 
 * Combines location tree + category tree lazy-loading in a single hook.
 * This is a pure wrapper around existing hooks - maintains exact current behavior:
 * - Location tree: Makes API calls on each expansion
 * - Category tree: Makes API calls on each expansion
 * 
 * Used by: CreateResourcePage, EditResourcePage, CreateCategoryPage, EditCategoryPage
 * 
 * @example
 * ```tsx
 * const {
 *   locationNodes,
 *   categoryNodes,
 *   handleLoadLocationChildren,
 *   handleLoadCategoryChildren,
 *   fetchCategoriesForLocation,
 * } = useAdminFormTrees();
 * 
 * // Pass to TreeSelect components:
 * <TreeSelect 
 *   nodes={locationNodes} 
 *   onLoadChildren={handleLoadLocationChildren} 
 *   ... 
 * />
 * <TreeSelect 
 *   nodes={categoryNodes} 
 *   onLoadChildren={handleLoadCategoryChildren} 
 *   ... 
 * />
 * ```
 */
export function useAdminFormTrees() {
  // Location tree state and lazy loader
  const [locationNodes, setLocationNodes] = useState<FlatTreeNode[]>([]);
  const handleLoadLocationChildren = useLocationTreeLazyLoader(setLocationNodes);

  // Category tree state and lazy loader
  const [categoryNodes, setCategoryNodes] = useState<FlatTreeNode[]>([]);
  const { fetchCategoriesForLocation, handleLoadCategoryChildren } = useParentCategoryTree(setCategoryNodes);

  return {
    // Location tree
    locationNodes,
    setLocationNodes,
    handleLoadLocationChildren,
    
    // Category tree
    categoryNodes,
    setCategoryNodes,
    fetchCategoriesForLocation,
    handleLoadCategoryChildren,
  };
}
